import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { WalletLink } from "./WalletLink";
import { Skeleton } from "./Skeleton";
import {
  cardsPerThousandPoints,
  formatCards,
  formatDateRange,
} from "../utils/format";

interface WalletAirdropsProps {
  walletAddress: string;
}

/**
 * Per-airdrop $CARDS received by a wallet, with the wallets ranked just above
 * and below it in that quarter's points so users can compare allocations.
 */
export function WalletAirdrops({ walletAddress }: WalletAirdropsProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["wallet-airdrops", walletAddress],
    queryFn: async () => {
      const { data, error } = await api
        .wallet({ walletAddress })
        .airdrops.get();
      if (error) throw error;
      return data;
    },
    enabled: !!walletAddress,
  });

  if (isLoading) {
    return <Skeleton kind="lines" rows={4} />;
  }

  if (error) {
    return (
      <p className="text-red-400 text-sm">
        Could not load airdrop data: {String(error)}
      </p>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <p className="text-gray-500">No $CARDS airdrops are being tracked yet.</p>
    );
  }

  return (
    <div className="space-y-6">
      {data.data.map((item) => {
        const { airdrop } = item;
        const ratio = cardsPerThousandPoints(item.amountReceived, item.pointsGained);
        const avgRatio = cardsPerThousandPoints(
          airdrop.totalDistributed,
          item.totalPlatformGain
        );
        const notIndexed = airdrop.recipientCount === 0;

        return (
          <div key={airdrop.id} className="card-inset p-4 space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-gray-50">
                  {airdrop.label}
                </h3>
                <p className="text-xs text-gray-500">
                  Points period {formatDateRange(airdrop.periodFrom, airdrop.periodTo)}
                  {", "}
                  {formatCards(airdrop.totalDistributed)} $CARDS to{" "}
                  {airdrop.recipientCount.toLocaleString()} wallets
                </p>
              </div>
              {notIndexed && (
                <span className="pill bg-gray-700 text-gray-400">
                  Not indexed yet
                </span>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-700/60">
                <div className="text-gray-400 text-xs mb-1">$CARDS received</div>
                <div className="text-lg font-semibold text-yellow-400 num">
                  {item.amountReceived !== null
                    ? formatCards(item.amountReceived)
                    : notIndexed
                    ? "—"
                    : "0"}
                </div>
                {item.amountReceived === null && !notIndexed && (
                  <div className="text-xs text-gray-500 mt-1">
                    No claim found
                  </div>
                )}
              </div>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-700/60">
                <div className="text-gray-400 text-xs mb-1">Airdrop rank</div>
                <div className="text-lg font-semibold text-gray-50 num">
                  {item.amountRank !== null ? `#${item.amountRank}` : "—"}
                </div>
                {item.amountRank !== null && (
                  <div className="text-xs text-gray-500 mt-1 num">
                    of {airdrop.recipientCount.toLocaleString()} recipients
                  </div>
                )}
              </div>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-700/60">
                <div className="text-gray-400 text-xs mb-1">Points gained</div>
                <div className="text-lg font-semibold text-gray-50 num">
                  {item.pointsGained !== null
                    ? item.pointsGained.toLocaleString()
                    : "—"}
                </div>
                {item.pointsRank !== null && (
                  <div className="text-xs text-gray-500 mt-1 num">
                    #{item.pointsRank} of {item.rankedWallets.toLocaleString()}
                    {item.sharePercent !== null &&
                      `, ${item.sharePercent.toFixed(2)}%`}
                  </div>
                )}
              </div>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-700/60">
                <div className="text-gray-400 text-xs mb-1">$CARDS per 1k points</div>
                <div className="text-lg font-semibold text-gray-50 num">
                  {ratio !== null ? formatCards(ratio) : "—"}
                </div>
                {avgRatio !== null && (
                  <div className="text-xs text-gray-500 mt-1 num">
                    platform avg {formatCards(avgRatio)}
                  </div>
                )}
              </div>
            </div>

            {/* Similar-points neighbours */}
            {item.neighbors.length > 0 ? (
              <div>
                <div className="text-sm text-gray-400 mb-2">
                  Wallets with similar points this quarter
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="table-head">
                        <th className="pb-2 pr-4 w-20 font-medium">Rank</th>
                        <th className="pb-2 pr-4 font-medium">Wallet</th>
                        <th className="pb-2 pr-4 text-right font-medium">Points gained</th>
                        <th className="pb-2 pr-4 text-right font-medium">Share</th>
                        <th className="pb-2 pr-4 text-right font-medium">$CARDS received</th>
                        <th className="pb-2 text-right font-medium">$CARDS / 1k pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.neighbors.map((n) => {
                        const nRatio = cardsPerThousandPoints(
                          n.amountReceived,
                          n.pointsGained
                        );
                        return (
                          <tr
                            key={n.walletAddress}
                            className={`table-row ${
                              n.isSelf
                                ? "bg-yellow-400/10 shadow-[inset_2px_0_0_0_theme(colors.yellow.400)]"
                                : ""
                            }`}
                          >
                            <td className="py-2 pr-4 text-gray-400 num">
                              #{n.pointsRank}
                            </td>
                            <td className="py-2 pr-4">
                              <span className="inline-flex items-center gap-2">
                                <WalletLink address={n.walletAddress} />
                                {n.isSelf && (
                                  <span className="pill bg-yellow-400/15 text-yellow-400">
                                    this wallet
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-right text-gray-100 num">
                              {n.pointsGained.toLocaleString()}
                            </td>
                            <td className="py-2 pr-4 text-right text-gray-300 num">
                              {n.sharePercent.toFixed(2)}%
                            </td>
                            <td className="py-2 pr-4 text-right font-medium num">
                              {n.amountReceived !== null ? (
                                <span className="text-yellow-400">
                                  {formatCards(n.amountReceived)}
                                </span>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </td>
                            <td className="py-2 text-right text-gray-300 num">
                              {nRatio !== null ? formatCards(nRatio) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                No points recorded for this wallet during the airdrop's points
                period, so there is nothing to compare against.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
